import React from "react";
import { Box, Typography, Chip, Button, Tooltip, Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Mail, ExternalLink, ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    root: {
      backgroundColor: isDark ? "#1e293b" : "#f8fafc",
      borderRadius: 12,
      border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
      padding: theme.spacing(2.5),
      marginBottom: theme.spacing(2),
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 8,
      marginBottom: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      borderBottom: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`
    },
    metaGrid: {
      display: "grid",
      gridTemplateColumns: "auto 1fr",
      rowGap: theme.spacing(0.75),
      columnGap: theme.spacing(1.5),
      fontSize: "0.85rem",
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
      padding: theme.spacing(1.5),
      borderRadius: 8,
      border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
      marginBottom: theme.spacing(2)
    },
    metaLabel: {
      fontWeight: 600,
      color: isDark ? "#94a3b8" : "#64748b"
    },
    metaValue: {
      color: isDark ? "#f1f5f9" : "#0f172a",
      fontWeight: 500,
      wordBreak: "break-all"
    },
    emailBodyCard: {
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
      padding: theme.spacing(2.5),
      borderRadius: 8,
      border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
      marginBottom: theme.spacing(2)
    },
    greeting: {
      fontWeight: 700,
      fontSize: "1rem",
      color: isDark ? "#f8fafc" : "#0f172a",
      marginBottom: theme.spacing(1.5)
    },
    paragraph: {
      fontSize: "0.88rem",
      lineHeight: 1.6,
      color: isDark ? "#cbd5e1" : "#334155",
      marginBottom: theme.spacing(1.5)
    },
    actionContainer: {
      textAlign: "center",
      margin: theme.spacing(2.5, 0)
    },
    signButton: {
      backgroundColor: "#2563eb",
      color: "#ffffff",
      fontWeight: 700,
      padding: "10px 24px",
      borderRadius: 8,
      textTransform: "none",
      fontSize: "0.92rem",
      boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
      "&:hover": {
        backgroundColor: "#1d4ed8"
      }
    },
    auditSection: {
      backgroundColor: isDark ? "rgba(22, 163, 74, 0.1)" : "#f0fdf4",
      border: `1px solid ${isDark ? "rgba(22, 163, 74, 0.3)" : "#bbf7d0"}`,
      borderRadius: 8,
      padding: theme.spacing(1.5),
      display: "flex",
      flexDirection: "column",
      gap: 6
    },
    auditTitle: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontWeight: 700,
      fontSize: "0.82rem",
      color: isDark ? "#4ade80" : "#166534"
    },
    auditItem: {
      fontSize: "0.78rem",
      color: isDark ? "#86efac" : "#15803d"
    }
  };
});

const DisparoEmailPreview = ({ disparo }) => {
  const classes = useStyles();

  if (!disparo) return null;

  const nomeColaborador = disparo.nome_funcionario || "Colaborador(a)";
  const emailDestino =
    disparo.email ||
    disparo.destinatario ||
    disparo.payload_envio?.detalhes_email?.destinatario ||
    "E-mail não informado";

  const linkAssinatura =
    disparo.link_assinatura ||
    disparo.gisign_envelopes?.link_assinatura;

  const resendId =
    disparo.payload_envio?.detalhes_email?.id ||
    disparo.detalhes_email?.id ||
    disparo.id;

  const motivoContingencia =
    disparo.motivo_fallback ||
    disparo.payload_envio?.motivo ||
    "Ausência de celular cadastrado (Fallback E-mail)";

  return (
    <Box className={classes.root}>
      {/* Cabeçalho do Card */}
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" gap={1}>
          <Mail size={16} color="#2563eb" />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            E-mail de Notificação (Contingência / Fallback GiSign)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            size="small"
            label="Provedor: Resend"
            style={{
              backgroundColor: "rgba(37, 99, 235, 0.1)",
              color: "#2563eb",
              fontWeight: 600,
              fontSize: "0.72rem",
              height: 22
            }}
          />
          <Chip
            size="small"
            icon={<ShieldCheck size={14} color="#16a34a" />}
            label="E-mail Entregue"
            style={{
              backgroundColor: "rgba(22, 163, 74, 0.12)",
              color: "#16a34a",
              fontWeight: 600,
              fontSize: "0.72rem",
              height: 22
            }}
          />
        </Box>
      </Box>

      {/* Metadados do E-mail (De / Para / Assunto) */}
      <Box className={classes.metaGrid}>
        <span className={classes.metaLabel}>De:</span>
        <span className={classes.metaValue}>
          GiSign Notificações &lt;noreply@gisign.com.br&gt; (via Resend Mailer)
        </span>

        <span className={classes.metaLabel}>Para:</span>
        <span className={classes.metaValue}>{emailDestino}</span>

        <span className={classes.metaLabel}>Assunto:</span>
        <span className={classes.metaValue} style={{ fontWeight: 700 }}>
          [GiSign Timbre] Pendência de Assinatura - Processo Admissional
        </span>
      </Box>

      {/* Caixa do Corpo do E-mail */}
      <Box className={classes.emailBodyCard}>
        <Typography className={classes.greeting}>
          Olá, {nomeColaborador}!
        </Typography>

        <Typography className={classes.paragraph}>
          Identificamos que a assinatura do seu documento de trabalho referente ao processo de admissão na <strong>Timbre</strong> ainda não foi concluída.
        </Typography>

        <Typography className={classes.paragraph}>
          Como não localizamos seu telefone celular com WhatsApp ativo para contato direto, estamos enviando este comunicado através do seu e-mail cadastrado para garantir que você possa formalizar sua contratação sem atrasos.
        </Typography>

        {linkAssinatura && (
          <Box className={classes.actionContainer}>
            <Button
              component="a"
              href={linkAssinatura}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<ExternalLink size={16} />}
              className={classes.signButton}
            >
              Acessar GiSign para Assinar Documento
            </Button>
          </Box>
        )}

        <Typography className={classes.paragraph} style={{ fontSize: "0.82rem", color: "#64748b" }}>
          * Reforçamos que a conclusão da assinatura é indispensável para a finalização do seu processo e liberação da data acordada.
        </Typography>
      </Box>

      {/* Selo de Auditoria e Comprovação de Envio */}
      <Box className={classes.auditSection}>
        <Box className={classes.auditTitle}>
          <CheckCircle2 size={16} />
          <span>Comprovante de Envio e Rastreabilidade</span>
        </Box>
        <span className={classes.auditItem}>
          <strong>Status da Entrega:</strong> Confirmado com sucesso pelo provedor transacional.
        </span>
        {resendId && (
          <span className={classes.auditItem}>
            <strong>ID da Mensagem Resend:</strong> {resendId}
          </span>
        )}
        <span className={classes.auditItem}>
          <strong>Motivo da Notificação via E-mail:</strong> {motivoContingencia}
        </span>
      </Box>
    </Box>
  );
};

export default DisparoEmailPreview;
